import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { gameManager } from 'src/app/game/businesslogic/GameManager';
import { GhsManager } from 'src/app/game/businesslogic/GhsManager';
import { SettingsManager, settingsManager } from 'src/app/game/businesslogic/SettingsManager';
import { EventCard, EventCardIdentifier } from 'src/app/game/model/data/EventCard';
import { EntitiesMenuDialogComponent } from 'src/app/ui/figures/entities-menu/entities-menu-dialog';
import { EventCardDialogComponent } from 'src/app/ui/figures/event/dialog/event-card-dialog';
import { EventCardDrawComponent } from 'src/app/ui/figures/event/draw/event-card-draw';
import { EventCardComponent } from 'src/app/ui/figures/event/event-card';
import { GhsLabelDirective } from 'src/app/ui/helper/label';
import { ghsDialogClosingHelper } from 'src/app/ui/helper/Static';
import { GhsTooltipDirective } from 'src/app/ui/helper/tooltip/tooltip';
import { TrackUUIDPipe } from 'src/app/ui/helper/trackUUID';

@Component({
  imports: [
    NgClass,
    FormsModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    GhsLabelDirective,
    GhsTooltipDirective,
    TrackUUIDPipe,
    EventCardComponent
  ],
  selector: 'ghs-event-card-deck',
  templateUrl: './event-card-deck.html',
  styleUrls: ['./event-card-deck.scss']
})
export class EventCardDeckComponent {
  private dialogRef = inject(DialogRef);
  private dialog = inject(Dialog);
  private ghsManager = inject(GhsManager);

  edition: string;
  type: string;
  types: string[] = [];
  upcomingCards: EventCard[] = [];
  newCards: EventCard[] = [];
  drawnCards: { identifier: EventCardIdentifier; card: EventCard | undefined }[] = [];

  filter: string = '';
  allTypes: boolean = false;
  allTypesToggle: boolean = false;
  resolved: boolean = false;
  setup: boolean = false;
  confirmReset: boolean = false;

  settingsManager: SettingsManager = settingsManager;

  data: { edition: string; type: string; setup: boolean } = inject(DIALOG_DATA);

  constructor() {
    this.ghsManager.uiChangeEffect(() => this.update());
    this.edition = this.data.edition;
    this.types = gameManager.eventCardManager
      .getEventTypesForEdition(this.edition)
      .filter((type) => this.allTypes || (gameManager.game.party.eventDecks[type] && gameManager.game.party.eventDecks[type].length));
    this.allTypesToggle = gameManager.eventCardManager.getEventTypesForEdition(this.edition).length !== this.types.length;
    this.type = this.data.type || this.types[0];
    this.setup = this.data.setup || false;
    this.update();
  }

  update() {
    const deck = gameManager.eventCardManager.getEventCardsForEdition(this.edition, this.type);
    const current = gameManager.game.party.eventDecks[this.type] || [];
    this.upcomingCards = current
      .map((cardId) => deck.find((e) => e.cardId === cardId))
      .filter((e) => e)
      .map((e) => e as EventCard);
    this.newCards = deck.filter(
      (e) => !current.includes(e.cardId) && !gameManager.game.party.eventCards.find((id) => id.type === this.type && id.cardId === e.cardId)
    );
    this.drawnCards = gameManager.game.party.eventCards
      .filter((id) => deck.find((e) => e.edition === id.edition && e.type === id.type && e.cardId === id.cardId))
      .map((id) => {
        return { identifier: id, card: deck.find((card) => card.cardId === id.cardId) };
      })
      .reverse();
  }

  selectType(type: string) {
    this.type = type;
    this.update();
  }

  toggleAllTypes() {
    this.allTypes = !this.allTypes;
    this.types = gameManager.eventCardManager
      .getEventTypesForEdition(this.edition)
      .filter((type) => this.allTypes || (gameManager.game.party.eventDecks[type] && gameManager.game.party.eventDecks[type].length));
  }

  shuffle() {
    gameManager.stateManager.before('events.deck.shuffle', this.type);
    gameManager.eventCardManager.shuffleEvents(this.type);
    gameManager.stateManager.after();
  }

  dropUpcoming(event: CdkDragDrop<EventCard[]>) {
    gameManager.stateManager.before('events.deck.reorder', this.type);
    if (event.container === event.previousContainer) {
      moveItemInArray(gameManager.game.party.eventDecks[this.type] || [], event.previousIndex, event.currentIndex);
    } else {
      (gameManager.game.party.eventDecks[this.type] || []).splice(
        event.currentIndex < 0 ? 0 : event.currentIndex,
        0,
        this.newCards[event.previousIndex].cardId
      );
    }
    gameManager.stateManager.after();
  }

  dropNew(event: CdkDragDrop<EventCard[]>) {
    gameManager.stateManager.before('events.deck.reorder', this.type);
    if (event.container === event.previousContainer) {
      moveItemInArray(this.newCards, event.previousIndex, event.currentIndex);
    } else {
      (gameManager.game.party.eventDecks[this.type] || []).splice(event.previousIndex, 1);
    }
    gameManager.stateManager.after();
  }

  dropDrawn(event: CdkDragDrop<EventCard[]>) {
    let eventCard: EventCard | undefined;
    if (event.previousContainer.id === 'new-list') {
      eventCard = this.newCards[event.previousIndex];
    } else if (event.previousContainer.id === 'upcoming-list') {
      eventCard = this.upcomingCards[event.previousIndex];
    }

    if (eventCard) {
      gameManager.stateManager.before('events.deck.markDrawn', eventCard.type, eventCard.cardId);
      gameManager.game.party.eventCards.splice(
        event.currentIndex,
        0,
        new EventCardIdentifier(eventCard.cardId, eventCard.edition, eventCard.type, -1, [], [], false, false)
      );
      gameManager.stateManager.after();
    }
  }

  removeCard(eventCard: EventCard) {
    gameManager.stateManager.before('events.deck.removeEvent', this.type, eventCard.cardId);
    gameManager.eventCardManager.removeEvent(eventCard.type, eventCard.cardId);
    gameManager.stateManager.after();
  }

  addCard(eventCard: EventCard) {
    gameManager.stateManager.before('events.deck.addEvent', this.type, eventCard.cardId);
    gameManager.eventCardManager.addEvent(eventCard.type, eventCard.cardId);
    gameManager.stateManager.after();
  }

  removeDrawn(id: EventCardIdentifier) {
    const index = gameManager.game.party.eventCards.indexOf(id);
    if (index !== -1) {
      gameManager.stateManager.before('events.deck.removeDrawn', this.type, id.cardId);
      gameManager.game.party.eventCards.splice(index, 1);
      gameManager.stateManager.after();
    }
  }

  updateDrawnSelect(id: EventCardIdentifier, change: EventCardIdentifier | undefined = undefined) {
    const newSelected = change ? change.selected : -1;
    const newSubSelections = change ? change.subSelections : [];
    if (
      id.selected !== newSelected ||
      id.subSelections.length !== newSubSelections.length ||
      id.subSelections.some((value) => !newSubSelections.includes(value))
    ) {
      gameManager.stateManager.before('events.deck.changeSelection', this.type, id.cardId);
      id.selected = newSelected;
      id.subSelections = newSubSelections;
      gameManager.stateManager.after(!change ? 750 : 0);
    }
  }

  showEventCard(eventCard: EventCard, id: EventCardIdentifier | undefined = undefined) {
    this.dialog.open(EventCardDialogComponent, {
      panelClass: ['fullscreen-panel'],
      disableClose: true,
      data: { eventCard: eventCard, interactive: true, id: id }
    });
  }

  drawEvent(cardId: string | undefined = undefined) {
    this.dialog
      .open(EventCardDrawComponent, {
        panelClass: ['dialog'],
        data: {
          edition: this.edition,
          type: this.type,
          cardId: cardId
        }
      })
      .closed.subscribe({
        next: (results: any) => {
          if (settingsManager.settings.eventsApply && results && results.length) {
            this.dialog.open(EntitiesMenuDialogComponent, {
              panelClass: ['dialog'],
              data: { eventResults: results }
            });
          }
        }
      });
  }

  resetDeck() {
    if (this.confirmReset) {
      gameManager.stateManager.before('events.deck.reset', this.type);
      if (!!gameManager.game.party.eventDecks[this.type]) {
        gameManager.game.party.eventDecks[this.type] = [];
        gameManager.game.party.eventCards = gameManager.game.party.eventCards.filter(
          (value) => value.edition !== this.edition && value.type !== this.type
        );
      }
      gameManager.eventCardManager.buildPartyDeck(this.edition, this.type);
      gameManager.stateManager.after();
      this.update();
    } else {
      this.confirmReset = true;
    }
  }

  close() {
    ghsDialogClosingHelper(this.dialogRef);
  }
}
